import { PrismaEnums } from '@airreading/schema/browser';
import { z } from 'zod';

import { PostCoachUsersCSVFormKeys as coachCsvFormKeys } from './data/coach-csv.js';
import { studentYearInputSchema } from './data/index.js';
import { StudentCsvCell, studentCsvCellFrom } from './data/student-csv.js';
import { studentTagSchema } from './data/student.js';

/**
 * Helper to transform string to boolean: yes -> true and no -> false
 */
export const booleanFromYesNo = z.union([
  z.literal('yes').transform(() => true),
  z.literal('no').transform(() => false),
]);

/**
 * This module is now deprecated, as newer data types fall under data service
 * conventions and live in `packages/common/src/data`. Avoid adding more items
 * in this module.
 */

export const putSchedulesStatusBody = z.object({
  status: z.nativeEnum(PrismaEnums.ScheduleStatus),
  scheduleIds: z.string().array(),
});
export type PutSchedulesStatusBody = z.infer<typeof putSchedulesStatusBody>;

export const getSchedulesByIdsBody = z.object({
  scheduleIds: z.array(z.string()),
});

/**
 * Student CSV cell that distinguishes blank, `clear`, and a value. Only values are validated.
 * Call `ctx.addIssue` and return `z.NEVER` from `parseValue` to reject a value.
 */
function studentCsvCellSchema<T>(parseValue: (value: string, ctx: z.RefinementCtx) => T) {
  // `undefined` is rejected, so a missing template column fails the row. Null and "" are blank cells.
  return z.union([z.string(), z.null()]).transform((raw, ctx): StudentCsvCell<T> => {
    const cell = studentCsvCellFrom(raw);
    return cell.kind === 'value' ? { kind: 'value', value: parseValue(cell.value, ctx) } : cell;
  });
}

const studentCsvTextSchema = studentCsvCellSchema((value) => value);

const fleidCSVSchema = studentCsvCellSchema((value, ctx) => {
  if (!/^[Ff][Ll]\d{12}$/.test(value)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Unrecognized FLEID format' });
    return z.NEVER;
  }
  return value.toUpperCase();
});

const gradeCSVSchema = studentCsvCellSchema((value, ctx) => {
  const parsed = studentYearInputSchema.safeParse(value);
  if (!parsed.success) {
    parsed.error.issues.forEach((issue) => ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue.message }));
    return z.NEVER;
  }
  return parsed.data;
});

const parentEmailCSVSchema = studentCsvCellSchema((value, ctx) => {
  if (!z.string().email().safeParse(value).success) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid parent email' });
    return z.NEVER;
  }
  return value;
});

const studentTagCSVSchema = studentCsvCellSchema((value) => {
  const tags: Array<z.infer<typeof studentTagSchema>> = [];
  for (const part of value.split(',')) {
    const parsed = studentTagSchema.safeParse(part.trim().toLowerCase());
    if (parsed.success && !tags.includes(parsed.data)) {
      tags.push(parsed.data);
    }
  }
  return tags;
});

/** Blank keeps the current value. Yes/No fields never accept `clear`. */
const yesNoCSVSchema = z.union([z.string(), z.null()]).transform((raw, ctx): boolean | null => {
  const answer = raw?.trim() ?? '';
  if (answer === '') return null;
  if (answer === 'yes') return true;
  if (answer === 'no') return false;
  ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Must be Yes or No' });
  return z.NEVER;
});

export const postStudentUserCSVRow = z
  .object({
    firstName: studentCsvTextSchema,
    lastName: studentCsvTextSchema,
    email: z
      .union([z.string(), z.null(), z.undefined()])
      .transform((val): string | null => {
        if (val == null) return null;
        const trimmed = val.trim();
        if (trimmed === '') return null;
        return trimmed.toLowerCase();
      })
      .pipe(z.string().email('Invalid email').nullable()),
    grade: gradeCSVSchema,
    specialEd: yesNoCSVSchema,
    dyslexic: yesNoCSVSchema,
    ell: yesNoCSVSchema,
    ['fleid (optional)']: fleidCSVSchema,
    ['Classroom teacher (optional)']: studentCsvTextSchema,
    ['studentTag (optional)']: studentTagCSVSchema,
    ['schedule (optional)']: studentCsvTextSchema,
    ['classTimeCT (optional)']: studentCsvTextSchema,
    ['School parent email (optional)']: parentEmailCSVSchema,
    ['School parent phone number (optional)']: studentCsvTextSchema,
  })
  .superRefine((data, ctx) => {
    if (data.email === null && data['fleid (optional)'].kind !== 'value') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FLEID is required for users without email',
        path: ['fleid (optional)'],
      });
    }
  });

export const PostStudentUsersCSVFormKeys = {
  studentsCSV: 'studentsCSV',
  schoolId: 'schoolId',
} as const;

export const postStudentUsersCSVBody = z.object({
  [PostStudentUsersCSVFormKeys.schoolId]: z.string({ message: 'School ID is required' }),
});

export const PostCoachUsersCSVFormKeys = coachCsvFormKeys;

/**
 * Roles supported by the test authentication endpoint (non-production only).
 * Used by POST /me/test-auth and automation tests.
 */
export const testAuthRoleSchema = z.enum(['admin', 'teacher', 'student']);
export type TestAuthRole = z.infer<typeof testAuthRoleSchema>;
export const TestAuthRole = testAuthRoleSchema.enum;

/**
 * Schema for test authentication endpoint (non-production only)
 * Allows generating Firebase custom tokens for test users
 */
export const testAuthRequestSchema = z
  .object({
    email: z.string().email('Invalid email').optional(),
    role: testAuthRoleSchema.optional(),
  })
  .refine((data) => data.email || data.role, {
    message: 'Either email or role must be provided',
  });
export type TestAuthRequest = z.infer<typeof testAuthRequestSchema>;

const mmddyyyyRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;

export const parseMMDDYYYY = (s: string): Date | null => {
  if (!mmddyyyyRegex.test(s)) return null;
  const [mm, dd, yyyy] = s.split('/').map(Number);
  const d = new Date(yyyy, mm - 1, dd);
  return d.getMonth() === mm - 1 && d.getDate() === dd ? d : null;
};

export const formatDateToISO = (d: Date): string => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const postCoachPaymentCSVRow = z.object({
  email: z.string(),
  ['pay_cycle']: z.string({ required_error: 'Missing pay period' }).trim().min(1, 'Missing pay period'),
  ['pay_date']: z.string({ required_error: 'Missing pay date' }).trim().min(1, 'Missing pay date'),
  ['payment_type']: z.string().trim().min(1, 'Missing payment type'),
  amount: z.preprocess(
    (val) => {
      if (typeof val !== 'string') return val;
      const normalized = val.trim().replaceAll('$', '').replaceAll(',', '');
      if (normalized === '') return Number.NaN;
      return Number(normalized);
    },
    z
      .number({ invalid_type_error: 'Amount must be a valid number' })
      .finite('Amount must be a valid number')
      .min(0.01, 'Amount must be at least 0.01'),
  ),
});

export const PostCoachPaymentCSVFormKeys = {
  paymentsCSV: 'paymentsCSV',
} as const;
